import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import SideMenu from '../../components/SideMenu';
import useDocTitle from '../../utils/useDocTitle';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import AudioPlayer from '../../components/AudioPlayer';
import ContactUs from '../../components/ContactUs';

const ResourceDetail = () => {
  const { resourceId } = useParams();
  const { language } = useLanguage();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocTitle(
    resource
      ? resource.title
      : language === 'English'
      ? 'Resource Details'
      : 'Detajet e Burimit'
  );

  useEffect(() => {
    const fetchResource = async () => {
      try {
        const response = await axios.get(
          `https://annexpilot.com/dashboard/wp-json/wp/v2/articles/${resourceId}`
        );

        const data = response.data;
        if (data.page_language[0].name !== language) {
          setResource(null); // Prevent rendering if the language does not match
        } else {
          setResource({
            id: data.id,
            title: data.title.rendered,
            content: data.article_content,
            imgSrc: data.main_image.guid,
            imgAlt: data.post_excerpt,
            docSrc: data.document.guid,
            audioSrc: data.audio_file?.guid,
          });
        }
      } catch (error) {
        console.error('Error fetching resource:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResource();
  }, [resourceId, language]);

  if (loading) {
    return (
      <div>
        {language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}
      </div>
    );
  }

  if (!resource) {
    return (
      <div>
        {language === 'English' ? 'Resource not found.' : 'Burimi nuk u gjet.'}
      </div>
    );
  }

  const menuItems = [
    { href: '#audio-player', label: language === 'English' ? 'Audio Description' : 'Përshkrimi Zanor' },
    { href: '#content', label: language === 'English' ? 'Article' : 'Artikulli' },
    { href: '#download', label: language === 'English' ? 'Download' : 'Shkarko' },
  ];

  const sanitizeAndAddTabIndex = (html) => {
    const sanitizedHTML = DOMPurify.sanitize(html, {
      ALLOWED_ATTR: ['class', 'src', 'alt', 'href', 'tabindex', 'img', 'p'],
    });
  
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedHTML, 'text/html');
    doc.querySelectorAll('p, h1, h2, h3, a, img').forEach((element) => {
      element.setAttribute('tabindex', '0');
      if (element.tagName === 'A') {
        element.setAttribute('target', '_blank'); // Open in a new tab
        element.setAttribute('rel', 'noopener noreferrer'); // Security best practice
        element.style.color = '#007BFF'; // Soft blue color for links
        element.style.fontWeight = '600'; // Soft bold style
        element.style.textDecoration = 'none'; // Remove underline if needed
      }
    });
  
    return doc.body.innerHTML;
  };

  const accessibleContent = sanitizeAndAddTabIndex(resource.content);

  return (
    <>
      <NavBar />

      <div
          className="relative w-full h-96 bg-cover bg-center flex items-center justify-center"
          style={{ backgroundImage: `url(${resource.imgSrc})` }}
        role="img"
        aria-label={
          resource.imgAlt || 
          (language === 'English' ? 'Resource background' : 'Sfundi i burimit')
        }
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-3xl md:text-5xl font-bold">{resource.title}</h1>
        </div>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen">
        <aside
          className="w-full md:w-1/4 bg-gray-100 px-8 py-12"
          aria-label={
            language === 'English'
              ? 'Side menu for resource details'
              : 'Menu anësore për detajet e burimit'
          }
        >
          <SideMenu
            backLink="/resources"
            backText={language === 'English' ? 'RESOURCES' : 'BURIMET'}
            menuItems={menuItems}
          />
        </aside>

        <main className="w-full md:w-3/4 py-8 px-4">
          {resource.audioSrc && (
            <section
              id="audio-player"
              className="container-inner mb-12 text-center"
              aria-labelledby="audio-player-heading"
            >
              <h2 id="audio-player-heading" className="sr-only">
                {language === 'English' ? 'Audio Player' : 'Luajtësi Audio'}
              </h2>
              <div className="mx-auto" style={{ width: '100%', maxWidth: '500px' }}>
                <AudioPlayer src={resource.audioSrc} />
              </div>
            </section>
          )}

          <section id="overview" className="container-inner mb-12">
            <div
              className="text-base md:text-lg text-gray-700 leading-relaxed"
              tabIndex="0"
              aria-label={
                language === 'English' ? 'Resource overview' : 'Përmbledhja e burimit'
              }
            >
              {resource.imgAlt}
            </div>
          </section>

          <section id="content" className="article-content mb-12">
            <h2 className="text-2xl font-semibold mb-4" tabIndex="0">
              {language === 'English' ? 'Article' : 'Artikulli'}
            </h2>
            <div dangerouslySetInnerHTML={{ __html: accessibleContent }} />
          </section>

          {resource.docSrc && (
            <section id="download" className="mt-12 download-section">
              <h2 className="text-2xl font-semibold mb-4" tabIndex="0">
                {language === 'English' ? 'Download' : 'Shkarko'}
              </h2>
              <div className="download-button-container">
                <a
                  href={resource.docSrc}
                  download
                  className="btn-custom"
                  aria-label={
                    language === 'English'
                      ? `Download the document for ${resource.title}`
                      : `Shkarko dokumentin për ${resource.title}`
                  }
                >
                  {language === 'English' ? 'Download Document' : 'Shkarko Dokumentin'}
                </a>
              </div>
            </section>
          )}

         <ContactUs />

        </main>
      </div>

      <Footer />
    </>
  );
};

export default ResourceDetail;